// Очередь генераций AI-инструментов: единственное место, где живёт цикл опроса KIE/Segmind.
//
// Зачем модуль существует. Раньше цикл опроса лежал в стейте компонента (pollKieResult скопирован
// пять раз, Segmind-поллер дважды). Инструменты на /ai-photo — условные siblings: переключил
// инструмент → компонент размонтирован → результат потерян; F5 — тоже. Здесь ключ задачи (taskId
// или poll_url) попадает в localStorage ДО первого опроса, поэтому задача переживает и переключение
// инструмента, и перезагрузку: вебхуки опроса без состояния, источник правды — KIE/Segmind.
//
// Модуль не-React и SSR-безопасный: на верхнем уровне нет ни одного обращения к window.
//
// ponytail: две вкладки браузера на /ai-photo поллят одну задачу и дважды пишут историю;
// cross-tab merge (как в airun.art) — когда дубли реально появятся.

import { n8nFetch } from './n8n-fetch';
import { WEBHOOK_ENDPOINTS } from '../config/webhook-urls';
import { addHistoryItem } from './imageHistoryDB';

export const STORAGE_KEY = 'ai-jobs';

const POLL_INTERVAL_MS = 5000;
const MAX_ATTEMPTS = 150;        // ~12.5 минут
const POLL_TIMEOUT_MS = 30000;   // таймаут одного запроса опроса
const MAX_BACKOFF_MS = 20000;
// Восстанавливать имеет смысл свежую задачу: зависшая в 'generating' запись иначе превратилась бы
// в вечный спиннер на чипе вкладки.
const MAX_RESTORE_AGE_MS = 24 * 60 * 60 * 1000;
// Segmind умеет вернуть результат голым base64 на сотни килобайт. В localStorage такое не едет:
// квота ~5 МБ на весь домен, а переполнение молчит — setItem бросает, и стор перестаёт сохраняться.
// В памяти url остаётся целым, обрезается только копия для localStorage.
const MAX_PERSISTED_URL = 2000;
const MAX_JOBS = 50;

const ERROR_TEXT = {
  sensitive: '🚫 Контент был помечен как чувствительный. Попробуйте изменить описание.',
  timeout: '⏰ Превышено время обработки. Повторите позже.',
  network: '🌐 Ошибка сети. Проверьте подключение к интернету и попробуйте еще раз.',
};

const EMPTY = [];

const listeners = new Set();
// Ключи задач с живым циклом опроса: второй цикл на тот же ключ — это два результата и две записи
// в истории.
const polling = new Set();
// id задачи → finalize(urls) из start(); не персистится.
const finalizers = new Map();

let jobs = [];
let snapshot = null;   // кэш стабильной ссылки для useSyncExternalStore
let ready = false;     // init отработал

// --- хранилище ------------------------------------------------------------------------------

// Мусор из localStorage (null, строки, записи без id) сюда не проходит: он доехал бы до рендера
// и уронил getJobs() внутри useSyncExternalStore — белый экран без выхода.
function readStore() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((j) => j && typeof j === 'object' && !Array.isArray(j) && j.id);
  } catch (e) {
    return [];
  }
}

// blob: живёт только до перезагрузки — в localStorage он был бы битой картинкой.
const isLongUrl = (u) => typeof u === 'string' && (u.length > MAX_PERSISTED_URL || u.startsWith('blob:'));

function persistable(job) {
  if (!Array.isArray(job.urls) || !job.urls.some(isLongUrl)) return job;
  return { ...job, urls: job.urls.map((u) => (isLongUrl(u) ? null : u)) };
}

function writeStore(items) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items.map(persistable)));
  } catch (e) {
    // quota / заблокированные данные сайта — очередь продолжает работать в памяти
  }
}

const isActive = (j) => j.status === 'queued' || j.status === 'generating';
const doneAt = (j) => j.finishedAt || j.startedAt || 0;

// Активные и ошибочные оставляем все; done — только новейший на инструмент (панель показывает
// ровно один результат, остальное живёт в истории IndexedDB); сверху общий потолок.
function prune() {
  const newest = new Map();
  for (const j of jobs) {
    if (j.status !== 'done') continue;
    const cur = newest.get(j.toolId);
    if (!cur || doneAt(j) > doneAt(cur)) newest.set(j.toolId, j);
  }
  jobs = jobs.filter((j) => j.status !== 'done' || newest.get(j.toolId) === j);
  if (jobs.length <= MAX_JOBS) return;
  const doomed = new Set(
    jobs
      .filter((j) => !isActive(j))
      .sort((a, b) => doneAt(a) - doneAt(b))
      .slice(0, jobs.length - MAX_JOBS)
      .map((j) => j.id)
  );
  jobs = jobs.filter((j) => !doomed.has(j.id));
}

function commit() {
  prune();
  writeStore(jobs);
  snapshot = null;
  listeners.forEach((fn) => {
    try { fn(); } catch (e) {}
  });
}

const findJob = (id) => jobs.find((j) => j.id === id) || null;

function updateJob(id, patch) {
  const i = jobs.findIndex((j) => j.id === id);
  if (i === -1) return null;
  jobs[i] = { ...jobs[i], ...patch };
  commit();
  return jobs[i];
}

// --- ошибки ----------------------------------------------------------------------------------

export function classifyError(msg) {
  const text = String(msg || 'Неизвестная ошибка');
  if (/flagged as sensitive|E005/.test(text)) return ERROR_TEXT.sensitive;
  if (/timeout|время ожидания/i.test(text)) return ERROR_TEXT.timeout;
  if (/network|fetch/i.test(text)) return ERROR_TEXT.network;
  return `❌ ${text}`;
}

function fail(id, msg) {
  finalizers.delete(id);
  updateJob(id, { status: 'error', error: classifyError(msg), finishedAt: Date.now() });
}

// --- разбор ответа Segmind --------------------------------------------------------------------

// Объединение двух копий waterfall'а из Upscale.js и ReplaceBackground.js. Разница между ними была
// только в имени ключа (image_out / image_5xeqw), поэтому здесь пробуются оба — параметра нет.
const SEGMIND_KEYS = ['image_out', 'image_5xeqw'];
const SEGMIND_FIELDS = ['image', 'url', 'result', 'output_image'];

const isImageString = (v) => typeof v === 'string' && (v.startsWith('http') || v.startsWith('data:'));

function fromSegmindArray(arr) {
  for (const item of arr) {
    if (!item || !SEGMIND_KEYS.includes(item.keyname) || !item.value) continue;
    if (typeof item.value === 'string') return item.value;
    if (item.value.data) return item.value.data;
  }
  return null;
}

export function extractSegmindOutput(data) {
  const d = data || {};
  for (const k of SEGMIND_KEYS) if (d[k]) return d[k];
  const out = d.output;
  if (Array.isArray(out)) return fromSegmindArray(out);
  if (typeof out !== 'string' || !out) return null;
  if (out.startsWith('http') && (out.includes('image') || out.includes('.jpg') || out.includes('.png'))) return out;
  try {
    const parsed = JSON.parse(out);
    if (Array.isArray(parsed)) return fromSegmindArray(parsed);
    if (parsed && typeof parsed === 'object') {
      for (const k of SEGMIND_KEYS) if (parsed[k]) return parsed[k];
      for (const f of SEGMIND_FIELDS) if (isImageString(parsed[f])) return parsed[f];
    }
    return null;
  } catch (e) {
    // Не JSON, но длинная строка — это голый base64 без префикса.
    if (out.length > 1000) return out.startsWith('data:') ? out : `data:image/jpeg;base64,${out}`;
    return null;
  }
}

function segmindFailMessage(r) {
  if (r.error_message) {
    return typeof r.error_message === 'object'
      ? (r.error_message.details || r.error_message.error || JSON.stringify(r.error_message))
      : String(r.error_message);
  }
  if (r.details) return String(r.details);
  return 'Workflow завершился с ошибкой';
}

// --- опрос -----------------------------------------------------------------------------------

function startPolling(id) {
  const job = findJob(id);
  if (!job || job.status !== 'generating' || !job.key) return;
  const key = job.key;
  if (polling.has(key)) return;
  polling.add(key);

  let attempts = 0;
  let errors = 0;
  const stop = () => polling.delete(key);
  const again = (delay) => {
    if (attempts >= MAX_ATTEMPTS) {
      stop();
      fail(id, 'Превышено время ожидания результата');
      return;
    }
    setTimeout(tick, delay);
  };

  async function tick() {
    // Задачу могли отменить (cancel удаляет запись) или завершить другим циклом.
    const current = findJob(id);
    if (!current || current.status !== 'generating') { stop(); return; }
    attempts++;

    let r;
    try {
      const kie = current.kind === 'kie';
      const res = await n8nFetch(kie ? WEBHOOK_ENDPOINTS.KIE_POLL : WEBHOOK_ENDPOINTS.SEGMIND_POLL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(kie ? { taskId: key } : { poll_url: key }),
        timeoutMs: POLL_TIMEOUT_MS,
      });
      if (!res || !res.ok) throw new Error(`poll ${res && res.status}`);
      const text = await res.text();
      if (!text) throw new Error('пустой ответ');
      r = JSON.parse(text) || {};
    } catch (e) {
      // Сеть/пустое тело/битый JSON — не повод хоронить задачу: она считается на стороне KIE.
      errors++;
      again(Math.min(POLL_INTERVAL_MS + 3000 * errors, MAX_BACKOFF_MS));
      return;
    }
    errors = 0;

    if (current.kind === 'kie') {
      if (r.code >= 400) { stop(); fail(id, r.error || r.message); return; }
      const s = r.data && r.data.state;
      if (s === 'success') {
        stop();
        const d = r.data;
        const url = d.image_5ro7 || (d.resultUrls && d.resultUrls[0]) || d.video_url || d.output_url;
        if (!url) { fail(id, 'KIE не вернул ссылку на результат'); return; }
        await finish(id, [url]);
        return;
      }
      if (s === 'failed') { stop(); fail(id, r.data.failMsg || r.data.failCode || 'Задача завершилась ошибкой'); return; }
      // Любое другое состояние (generating, waiting, пусто) — продолжаем опрос.
      again(POLL_INTERVAL_MS);
      return;
    }

    if (r.status === 'COMPLETED') {
      stop();
      const url = extractSegmindOutput(r);
      if (!url) { fail(id, 'Результат обработки не найден в ответе workflow'); return; }
      await finish(id, [url]);
      return;
    }
    if (r.status === 'FAILED') { stop(); fail(id, segmindFailMessage(r)); return; }
    again(POLL_INTERVAL_MS);
  }

  // Первый опрос сразу: у восстановленной задачи результат, скорее всего, уже готов.
  setTimeout(tick, 0);
}

// --- завершение -------------------------------------------------------------------------------

async function finish(id, urls) {
  const job = findJob(id);
  if (!job) return;
  let list = (Array.isArray(urls) ? urls : [urls]).filter(Boolean);
  // finalize — постобработка результата в браузере (склейка инпейнтинга). Живёт только в памяти:
  // после перезагрузки её нет, и задача завершается сырым результатом.
  const fin = finalizers.get(id);
  finalizers.delete(id);
  if (fin) {
    try {
      list = (await fin(list)).filter(Boolean);
    } catch (e) {
      console.warn('aiJobs: finalize не удался, отдаём сырой результат', e);
    }
  }
  // Отменили, пока шёл finalize — результат никому не нужен, в историю не пишем.
  if (!findJob(id)) {
    list.forEach((u) => typeof u === 'string' && u.startsWith('blob:') && URL.revokeObjectURL(u));
    return;
  }
  if (job.history) {
    try {
      await addHistoryItem(job.history.storeKey, job.history.meta || {}, list, {
        mediaType: job.history.mediaType || 'image',
      });
    } catch (e) {
      // Сбой записи в историю не отменяет готовый результат — он уже есть в панели.
      console.warn('aiJobs: не удалось записать историю', e);
    }
  }
  updateJob(id, { status: 'done', urls: list, finishedAt: Date.now(), error: null });
}

// --- восстановление ---------------------------------------------------------------------------

function init() {
  if (ready) return;
  ready = true;
  const now = Date.now();
  const resume = [];
  jobs = readStore().map((raw) => {
    // persistable() заменяет длинные url на null — потребителям с `urls.length > 0` они не нужны.
    const job = { ...raw, urls: (Array.isArray(raw.urls) ? raw.urls : []).filter(Boolean) };
    if (!isActive(job)) return job;
    // Без ключа восстанавливать нечего: вебхук создания либо не ответил, либо ответ потерян с вкладкой.
    if (!job.key) {
      return { ...job, status: 'error', error: classifyError('Задача прервана перезагрузкой страницы'), finishedAt: now };
    }
    if (now - (job.startedAt || 0) > MAX_RESTORE_AGE_MS) {
      return { ...job, status: 'error', error: classifyError('Превышено время ожидания результата'), finishedAt: now };
    }
    resume.push(job.id);
    return { ...job, status: 'generating' };
  });
  commit();
  resume.forEach(startPolling);
}

// --- публичный API ----------------------------------------------------------------------------

// run возвращает taskId (kie), poll_url (segmind) или массив готовых url (local). Исключение
// внутри run = ошибка задачи. Возвращает id сразу, не дожидаясь ни ответа вебхука, ни результата.
export function start({ toolId, kind, label, history, run, finalize }) {
  init();
  const id = `job_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
  if (finalize) finalizers.set(id, finalize);
  jobs.unshift({
    id,
    toolId,
    kind,
    key: null,
    label: label || '',
    status: 'queued',
    startedAt: Date.now(),
    finishedAt: null,
    urls: [],
    error: null,
    history: history || null,
  });
  commit();

  // Дальше — фоном: вызывающий уже получил id и мог размонтироваться.
  (async () => {
    try {
      const res = await run();
      if (!findJob(id)) return;   // отменили, пока ждали
      if (kind === 'local') {
        await finish(id, res);
        return;
      }
      const key = String(res || '');
      if (!key) throw new Error('Сервер не вернул идентификатор задачи');
      // Ключ ложится в localStorage ДО первого опроса — это и есть фикс перезагрузки.
      updateJob(id, { key, status: 'generating' });
      startPolling(id);
    } catch (e) {
      fail(id, e && e.message);
    }
  })();

  return id;
}

function drop(id) {
  finalizers.delete(id);
  const job = findJob(id);
  if (!job) return;
  if (job.key) polling.delete(job.key);
  jobs = jobs.filter((j) => j.id !== id);
  commit();
}

// cancel — для активной задачи (генерация на KIE доигрывает, результат забирать некому),
// remove — для завершённой. Разница только в имени на кнопке.
export const cancel = drop;
export const remove = drop;

export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// Ссылка стабильна, пока стор не менялся — иначе useSyncExternalStore зациклится.
// init отсюда НЕ зовём: getJobs дёргается во время рендера.
export function getJobs() {
  if (!ready) return EMPTY;
  if (!snapshot) snapshot = jobs.slice();
  return snapshot;
}

export const getServerJobs = () => EMPTY;

// Импорт модуля (TabBar на любом роуте) поднимает стор и возобновляет опрос. typeof-проверка —
// не обращение к window, при SSR ветка не выполняется; работа отложена в макрозадачу, чтобы во
// время импорта ничего не происходило.
if (typeof window !== 'undefined') setTimeout(init, 0);
